import { ChangeEventHandler, FC, ReactNode, useRef } from 'react';

import XUIDropdown, { XUIDropdownToggled, XUIDropdownHeader, XUIDropdownFooter } from '@xero/xui/react/dropdown';
import XUIIcon from '@xero/xui/react/icon';
import searchIcon from '@xero/xui-icon/icons/search';
import plusIcon from '@xero/xui-icon/icons/plus';
import XUITextInput from '@xero/xui/react/textinput';
import XUIButton from '@xero/xui/react/button';
import XUIPickList, { XUIPickitem } from '@xero/xui/react/picklist';
import './index.css';
import invalid from '@xero/xui-icon/icons/invalid';

interface IDropDownSelectProps {
    buttonContent: ReactNode;
    buttonLabel?: ReactNode;
    leftIcon?: ReactNode;
    inputPlaceHolder?: string;
    qaHook?: string;
    showSearchBox?: boolean;
    showFooter?: boolean;
    footerActionText?: string;
    className?: string;
    footerActionOnClick?: () => void;
    onInputChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: () => void;
    onClick?: () => void;
    hideCaret?: boolean;
    isInvalid?: boolean;
    validationMessage?: string;
    hideInvalidIcon?: boolean;
}

export const DropDownSelect: FC<IDropDownSelectProps> = (props) => {
    const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
    const dropdownRef = useRef<XUIDropdownToggled | null>(null);
    const focusInput = () => {
        inputRef.current?.focus();
    };

    const handleInputRef = (i: HTMLTextAreaElement | HTMLInputElement) => {
        // @ts-ignore
        inputRef.current = i;
    };

    const handleDropdownRef = (i: XUIDropdownToggled | null) => {
        // @ts-ignore
        dropdownRef.current = i;
    };

    const closeDropdownAndManageAction = () => {
        if (dropdownRef.current === null) {
            throw new Error('Unable to close the dropdown as the dropdownRef is null');
        }
        dropdownRef.current?.closeDropdown();
        if (props.footerActionOnClick) {
            props.footerActionOnClick();
        }
    };

    const dropdownButtonClasses = props.isInvalid
        ? 'searchable-button--caret invalid-dropdown-button'
        : 'searchable-button--caret';
    const trigger = (
        <XUIButton className={dropdownButtonClasses} qaHook={props.qaHook} hasCaret={!props.hideCaret}>
            {<span>{props.buttonContent}</span>}
        </XUIButton>
    );

    const searchBox = (
        <XUIDropdownHeader>
            <XUITextInput
                containerClassName='search-list container'
                isBorderlessSolid={true}
                placeholder={props.inputPlaceHolder ?? 'Search'}
                isFieldLayout={false}
                isLabelHidden={true}
                leftElement={<XUIIcon icon={searchIcon} className='xui-margin-left-small xui-margin-right-xlarge' />}
                onChange={props.onInputChange}
                inputRef={handleInputRef}
            />
        </XUIDropdownHeader>
    );

    const dropdownFooter = (
        <XUIDropdownFooter
            pickItems={
                <XUIPickitem
                    id='footerAction'
                    leftElement={<XUIIcon icon={plusIcon} className='xui-margin-right-xsmall' color='blue' />}
                    onClick={closeDropdownAndManageAction}
                >
                    {<span className='blue-text'> {props.footerActionText}</span>}
                </XUIPickitem>
            }
        />
    );

    const selectionBox = (
        <XUIDropdown
            qaHook={props.qaHook}
            header={props.showSearchBox ? searchBox : <></>}
            footer={props.showFooter ? dropdownFooter : <></>}
            hasFixedWidth={true}
            className='search-box'
        >
            <XUIPickList>{props.children}</XUIPickList>
        </XUIDropdown>
    );
    return (
        <div className='drop-down-searcher'>
            {props.buttonLabel && (
                <div className='xui-heading-separator xui-text-emphasis xui-margin-top'>{props.buttonLabel}</div>
            )}
            <XUIDropdownToggled
                ref={handleDropdownRef}
                trigger={trigger}
                dropdown={selectionBox}
                matchTriggerWidth={true}
                onClose={props.onBlur}
                onOpenAnimationEnd={focusInput}
                className={props.className}
            />
            {props.isInvalid && (
                <div
                    className={
                        'xui-validation xui-validation-layout xui-validation-is-invalid left-align-text' +
                        (props.hideInvalidIcon && ' xui-padding-left')
                    }
                >
                    {!props.hideInvalidIcon && <XUIIcon icon={invalid} />}
                    {props.validationMessage}
                </div>
            )}
        </div>
    );
};
